













import { Component, Prop, Vue } from "@/shared/vuepack";
import {
  DrivewealthAccountIdentifierArgs,
  RateOfReturnChartPoint,
} from "@/shared/types";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { useDrivewealthAccountPerformanceStore } from "@/shared/store/drivewealthAccountPerformance";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";

@Component({
  components: {
    GenericLoadingSpinner,
    GChart
  },
})
export default class RateOfReturnBarChart extends Vue {
  @Prop({ default: 220 })
  height: number;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop({ required: true })
  accountId: number;

  themeStore = useThemeStore();
  drivewealthAccountPerformanceStore = useDrivewealthAccountPerformanceStore();

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      color: this.themeStore.tertiaryColor,
      tooltip: {
        isHtml: true,
      },
      chartArea: {
        height: "85%",
      },
      hAxis: {
        gridlines: {
          color: "transparent"
        },
      },
      vAxis: {
        baselineColor: "black",
        format: "##%",
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  mounted (): void {
    this.drivewealthAccountPerformanceStore.getRateOfReturn(this.accountIdentifiers);
  }

  get isLoaded (): boolean {
    return this.drivewealthAccountPerformanceStore.rateOfReturnLoaded(this.accountId);
  }

  get showLoadingSpinner (): boolean {
    return !this.isLoaded;
  }

  get accountIdentifiers (): DrivewealthAccountIdentifierArgs {
    return {
      username: this.username,
      partnerId: this.partnerId,
      accountId: this.accountId
    };
  }

  get chartData (): RateOfReturnChartPoint[] {
    const rateOfReturnData = this.drivewealthAccountPerformanceStore.rateOfReturnResult(this.accountId);

    if (rateOfReturnData.emptyDataSetMessage) {
      return [];
    }

    return [
      {
        label: "MTD",
        value: rateOfReturnData.mtdRateOfReturn
      },
      {
        label: "1 Month",
        value: rateOfReturnData.oneMonthRateOfReturn
      },
      {
        label: "3 Month",
        value: rateOfReturnData.threeMonthRateOfReturn
      },
      {
        label: "6 Month",
        value: rateOfReturnData.sixMonthRateOfReturn
      },
      {
        label: "YTD",
        value: rateOfReturnData.ytdRateOfReturn
      },
      {
        label: "3 Year",
        value: rateOfReturnData.threeYearRateOfReturn
      },
      {
        label: "5 Year",
        value: rateOfReturnData.fiveYearRateOfReturn
      },
    ];
  }

  tooltipFormatter (chartPoint: RateOfReturnChartPoint): string {
    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${chartPoint.label}
                        </div>
                        <div>
                          Rate of Return:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${`${chartPoint.value}%`}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartPoint (chartPoint: RateOfReturnChartPoint): any {
    return [
      chartPoint.label,
      (chartPoint.value / 100),
      `stroke-color: ${this.themeStore.tertiaryColor}; color: ${this.themeStore.tertiaryColor}`,
      this.tooltipFormatter(chartPoint),
    ];
  }

  get displayGraph (): boolean {
    return this.isLoaded && !!this.chartData.length;
  }

  get formattedChartData (): any {
    const formattedChartPoints = this.chartData.map(this.formatChartPoint);

    return [
      [
        "Time Period",
        "Rate of Return",
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
